import React, { useContext, useEffect, useState } from 'react';
import { TeApiContext, useSelector } from '@ecommerce/nest-ssr/common';
import CarouselComponentBasic from './carousel-basic';
import CarouselThumbItem from './components/carousel-thumb-item/carousel-thumb-item';
import classNames from 'classnames';
import PhotosInterface from 'interfaces/photo.interface';
import OfferInterface from 'interfaces/offer.interface';
import { OffersState } from '../../../../../../../reducers/offers';
import { CATEGORY_EXCEPTIONS_IDS } from 'modules/product/category-exceptions';
import { SeoConfigInterface } from 'modules/seo-module/interfaces/seo-config.interface';
import { getProductModalPhotoURL, getProductPreviewPhotoURL } from 'modules/product/helpers/product-image';
import getPromotedOffer from 'helpers/get-promoted-offer';
import offersPrepare from 'helpers/offers-prepare';
import { ProductInterface } from 'modules/product/interfaces/product.interface';
import { ProductUrlParams } from 'modules/product/interfaces/product-url-params.interface';
import { SeoLinkRelEnum } from 'modules/seo-module/interfaces/seo-link-rel.enum';
import { LinkTarget } from 'atomic/link/link-target.enum';

const Carousel: React.FC<{
    categoryId: string;
    photos: PhotosInterface[];
    alt: string;
    title: string;
    emulateTouch: boolean;
    onClick?: (index: number) => void;
    modalCarousel?: boolean;
    showThumbs: boolean;
    className?: string;
    showStatus?: boolean;
    showArrows?: boolean;
    altWithCounter?: boolean;
    selectedItem?: number;
}> = ({
    photos,
    alt,
    title,
    emulateTouch,
    onClick,
    modalCarousel = false,
    showThumbs,
    className,
    showStatus,
    showArrows,
    altWithCounter,
    selectedItem = 0,
    categoryId
}) => {
    const [CarouselComponent, setCarousel] = useState<React.ComponentType<any>>(() => CarouselComponentBasic);
    const api = useContext(TeApiContext);
    const isMainImageLinkingToShop = useSelector(
        (state: { productBooster?: SeoConfigInterface; }) => state.productBooster?.mainImageLinkingToShop
    );

    const promotedDealers = useSelector(
        (state: { promotedDealers?: SeoConfigInterface; }) => state.promotedDealers
    );

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';
    const images = modalCarousel || isMobile ? photos : photos.slice(0, 4);
    const offers: OfferInterface[] = useSelector((state: { product: ProductInterface; offers: OffersState; }) => (
        state.offers?.prepared?.length ? state.offers?.prepared : offersPrepare(state.product.offers.items)
    ));

    const productUrlParams = useSelector(
        (state: { productUrlParams?: ProductUrlParams; }) => state.productUrlParams
    );

    const offer: OfferInterface = getPromotedOffer(offers, productUrlParams, promotedDealers?.promotedDealers);
    const linkingTest = isMainImageLinkingToShop || CATEGORY_EXCEPTIONS_IDS.includes(categoryId);

    useEffect(() => {
        import('react-responsive-carousel-2').then(mod => {
            setCarousel(() => mod.Carousel);
        });
    }, []);

    const renderCustomThumbs = () => {
        const totalPhotos = photos.length;

        return images.map((image, index) =>
            <CarouselThumbItem
                key={image.thumb}
                alt={alt}
                image={image.thumb}
                totalPhotos={totalPhotos}
                index={index}
                onClick={() => onClick ? onClick(index) : void 0}
                modalCarousel={modalCarousel}
            />
        );
    };

    return (
        <CarouselComponent
            showArrows={showArrows}
            showStatus={showStatus || false}
            statusFormatter={(current: number, total: number): string => `${current}/${total}`}
            emulateTouch={emulateTouch}
            showThumbs={showThumbs}
            showIndicators={false}
            className={classNames({
                'carousel-root--mobile': isMobile,
                [String(className)]: Boolean(className)
            })}
            renderThumbs={renderCustomThumbs}
            selectedItem={selectedItem}
            verticalSwipe="natural"
            thumbWidth={76}
        >
            {
                images.map((item: PhotosInterface, index: number) => (
                    React.createElement(linkingTest ? 'a' : 'div', {
                        href: linkingTest ? offer?.url : void 0,
                        key: item.large,
                        onClick: () => (onClick && !linkingTest) ? onClick(index) : void 0,
                        target: linkingTest ? LinkTarget.BLANK : void 0,
                        rel: linkingTest ? SeoLinkRelEnum.NOFOLLOW_NOOPENER_NOREFERRER : void 0,
                        className: classNames({
                            'slide__content': true,
                            'gtm_gp_open': true,
                            'slide__content--modal': modalCarousel,
                            'gtm_or_gallery': linkingTest
                        })
                    }, (
                        <>
                            <img
                                src={modalCarousel ? getProductModalPhotoURL(item) : getProductPreviewPhotoURL(item)}
                                alt={altWithCounter ? `${alt}${modalCarousel ? '' : ' - miniaturka'} - grafika ${index + 1}` : alt}
                                title={title}
                                height={modalCarousel ? void 0 : isMobile ? 162 : 309}
                                width={modalCarousel ? void 0 : isMobile ? 162 : 309}
                                loading={(index === 0) ? 'eager' : 'lazy'}
                                // @ts-expect-error - ts doesn't support this attribute
                                // eslint-disable-next-line
                                fetchpriority={(index === 0) ? 'high' : 'low'}
                            />
                            <p
                                className={classNames({
                                    'legend': true,
                                    'legend--thumb': photos.length > 1
                                })}
                            />
                        </>
                    ))
                ))
            }
        </CarouselComponent>
    );
};

export default Carousel;
